import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Banner, Button, Field, PageHead, StateBlock, useToast } from '@w7/ui'
import { authHeaders } from '@/auth/session'
import { cmApi } from '@/api/cmApi'
import { useCmMe } from '@/api/hooks/useCmMe'
import { canManageAccessRoles } from '@/app/staffAccess'
import type { FpPageFeature } from '@/features/users/fpAuthorise'
import './users.css'

const ROLE = 'Company Master'

export function ManageAccessRolesPage() {
  const toast = useToast()
  const me = useCmMe()
  const allowed = canManageAccessRoles(me.data?.caller)
  const [pages, setPages] = useState<FpPageFeature[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [newPage, setNewPage] = useState('')
  const [newUrl, setNewUrl] = useState('')
  const [newMenu, setNewMenu] = useState('Member')
  const [newFeatureLabel, setNewFeatureLabel] = useState('')
  const [newFeatureCode, setNewFeatureCode] = useState('')
  const [targetPage, setTargetPage] = useState('')

  useEffect(() => {
    if (!allowed) {
      setLoading(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const data = await cmApi.staffRoleslistGet(authHeaders(), { roleName: ROLE })
        if (!cancelled) {
          setPages(data.pageFeatures ?? [])
          setTargetPage(data.pageFeatures?.[0]?.pageName ?? '')
        }
      } catch (err) {
        toast.error('Could not load roleslist', err instanceof Error ? err.message : undefined)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [allowed, toast])

  if (!allowed) {
    return (
      <div className="w7-users-page">
        <PageHead title="Manage Access Roles" subtitle="Company Master roleslists" />
        <Banner tone="error">Owner or staff.write required.</Banner>
      </div>
    )
  }

  const addPage = () => {
    const pageName = newPage.trim()
    if (!pageName) {
      toast.error('Page name required')
      return
    }
    if (pages.some((p) => p.pageName === pageName)) {
      toast.error('Page already exists')
      return
    }
    const url =
      newUrl.trim() ||
      `/${pageName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
    setPages((prev) => [
      ...prev,
      { pageName, url, menu: newMenu.trim() || 'Other', feature: [], features: [] },
    ])
    setTargetPage(pageName)
    setNewPage('')
    setNewUrl('')
  }

  const addFeature = () => {
    const label = newFeatureLabel.trim()
    const code = newFeatureCode.trim()
    if (!targetPage || !label || !code) {
      toast.error('Select page and enter feature label + code')
      return
    }
    setPages((prev) =>
      prev.map((p) => {
        if (p.pageName !== targetPage) return p
        if ((p.features ?? []).some((f) => f.code === code || f.label === label)) return p
        const features = [...(p.features ?? []), { label, code }]
        return { ...p, features, feature: features.map((f) => f.label) }
      }),
    )
    setNewFeatureLabel('')
    setNewFeatureCode('')
  }

  const removePage = (pageName: string) => {
    setPages((prev) => prev.filter((p) => p.pageName !== pageName))
  }

  const removeFeature = (pageName: string, code: string) => {
    setPages((prev) =>
      prev.map((p) => {
        if (p.pageName !== pageName) return p
        const features = (p.features ?? []).filter((f) => f.code !== code)
        return { ...p, features, feature: features.map((f) => f.label) }
      }),
    )
  }

  const onSave = async () => {
    setSaving(true)
    try {
      const saved = await cmApi.staffRoleslistUpsert(authHeaders(), {
        roleName: ROLE,
        pageFeatures: pages,
      })
      setPages(saved.pageFeatures ?? [])
      toast.success('Company Master roleslist saved')
    } catch (err) {
      toast.error('Save failed', err instanceof Error ? err.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="w7-users-page">
      <PageHead
        title="Manage Access Roles"
        subtitle="FP Lab–style roleslists for Company Master — pages and features"
        actions={
          <>
            <Link to="/users" className="w7-users-link">
              Back to users
            </Link>
            <Button variant="primary" onClick={() => void onSave()} disabled={saving || loading}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      />

      <Banner tone="info">
        Role <strong>{ROLE}</strong> (FP Super Admin equivalent). Staff create/edit
        loads these pages; selected features store as{' '}
        <code>{'{ pageName, url, feature[] }'}</code>.
      </Banner>

      {loading ? (
        <StateBlock icon="users" title="Loading roleslist…" />
      ) : (
        <>
          <section className="w7-users-block">
            <header className="w7-users-block-head">
              <h2>Add page</h2>
            </header>
            <div className="w7-users-toolbar" style={{ flexWrap: 'wrap', gap: 12 }}>
              <Field label="Menu">
                {({ id: fid }) => (
                  <input
                    id={fid}
                    className="w7-input"
                    value={newMenu}
                    onChange={(e) => setNewMenu(e.target.value)}
                  />
                )}
              </Field>
              <Field label="Page name">
                {({ id: fid }) => (
                  <input
                    id={fid}
                    className="w7-input"
                    value={newPage}
                    onChange={(e) => setNewPage(e.target.value)}
                  />
                )}
              </Field>
              <Field label="URL">
                {({ id: fid }) => (
                  <input
                    id={fid}
                    className="w7-input"
                    value={newUrl}
                    onChange={(e) => setNewUrl(e.target.value)}
                    placeholder="/member-list"
                  />
                )}
              </Field>
              <Button onClick={addPage}>Add page</Button>
            </div>
          </section>

          <section className="w7-users-block">
            <header className="w7-users-block-head">
              <h2>Add feature</h2>
            </header>
            <div className="w7-users-toolbar" style={{ flexWrap: 'wrap', gap: 12 }}>
              <Field label="Page">
                {({ id: fid }) => (
                  <select
                    id={fid}
                    className="w7-select"
                    value={targetPage}
                    onChange={(e) => setTargetPage(e.target.value)}
                  >
                    {pages.map((p) => (
                      <option key={p.pageName} value={p.pageName}>
                        {p.pageName}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              <Field label="Feature label">
                {({ id: fid }) => (
                  <input
                    id={fid}
                    className="w7-input"
                    value={newFeatureLabel}
                    onChange={(e) => setNewFeatureLabel(e.target.value)}
                  />
                )}
              </Field>
              <Field label="Code">
                {({ id: fid }) => (
                  <input
                    id={fid}
                    className="w7-input"
                    value={newFeatureCode}
                    onChange={(e) => setNewFeatureCode(e.target.value)}
                    placeholder="downline.view"
                  />
                )}
              </Field>
              <Button onClick={addFeature}>Add feature</Button>
            </div>
          </section>

          <ul className="w7-users-menus">
            {pages.map((p) => (
              <li key={p.pageName} className="w7-users-menu-block">
                <div className="w7-users-role-head" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <strong>
                    {p.menu ? `${p.menu} → ` : ''}
                    {p.pageName}
                  </strong>
                  <span className="w7-muted">{p.url}</span>
                  <Button variant="ghost" size="sm" onClick={() => removePage(p.pageName)}>
                    Remove page
                  </Button>
                </div>
                <div className="w7-users-grant-grid">
                  {(p.features ?? []).map((f) => (
                    <label key={f.code} className="w7-users-grant">
                      <span>
                        {f.label} <code>{f.code}</code>
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => removeFeature(p.pageName, f.code)}>
                        Remove
                      </Button>
                    </label>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}
